import { useCallback } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { TIMEOUT_ORDER_LONGAMS } from '@app/api/client';
import { shellKeys } from '@app/api/hooks/shell';
import { get, post, withPath, type ApiData } from '@app/api/typed';
import type { components } from '@app/api/types';
import { approvalsKeys } from '@app/main/Approvals/Approvals.api';
import { contractsKeys } from '@app/main/Contracts/Contracts.api';
import { identitiesKeys } from '@app/main/Identities/Identities.api';
import { overviewKeys } from '@app/main/Overview/Overview.api';
import { READING_POLL_MS } from '@app/reading';

/**
 * Hooks de T15 (Runtime Status). O contrato está congelado: as leituras são
 * `getRuntimeAggregate`, `getRuntimeStatus` e `getRuntimeAuthorization`; as
 * ações, `materializeAuthorization`, `reconcileAdmission` e
 * `deployIdentityImage`.
 */

export type RuntimeAggregate = ApiData<'/api/v1/runtime/status'>;
export type RuntimeStatus = ApiData<'/api/v1/runtime/identities/{id}/status'>;
export type AuthorizationView = ApiData<'/api/v1/runtime/authorization'>;
export type OrderResult = ApiData<'/api/v1/runtime/materialize', 'post'>;
export type TriEstado = components['schemas']['TriEstado'];
export type CheckId = keyof RuntimeStatus['checks'];
export type Difference = AuthorizationView['differences'][number];

export const runtimeKeys = {
  todos: ['runtime'] as const,
  agregado: ['runtime', 'agregado'] as const,
  // A variante rápida (`?source=cache`). Fica **debaixo** de `agregado` de
  // propósito: quem invalida o agregado depois de uma ordem invalida as duas.
  agregadoCache: ['runtime', 'agregado', 'cache'] as const,
  status: (id: string) => ['runtime', 'status', id] as const,
  autorizacao: ['runtime', 'autorizacao'] as const,
};

/**
 * Agregado do cabeçalho: quantas identidades estão prontas e quantas divergem.
 *
 * É a **leitura viva** — o backend lê o cluster nesta chamada (`source=live`,
 * o padrão do contrato). No laboratório ela leva ~11 s; quem precisa desenhar
 * antes disso usa `useRuntimeAggregateCached` ao lado. T9 (`PendingTab`) espera
 * por esta, e só por esta, para refazer a fila com o selo.
 */
export function useRuntimeAggregate(): UseQueryResult<RuntimeAggregate, ApiError> {
  return useQuery<RuntimeAggregate, ApiError>({
    queryKey: runtimeKeys.agregado,
    queryFn: () => get('/api/v1/runtime/status'),
  });
}

/**
 * O mesmo agregado, **como o backend o conhece agora** (`?source=cache`, 04/09):
 * responde na hora, sem tocar o executor, com `stale: true` e `as_of` de quando
 * foi lido. `as_of: null` é «ninguém leu ainda» — `applications` vazio aí não
 * quer dizer que não há identidades, e a tela não pode dizer isso.
 *
 * `staleTime: 0`: o que está em cache no servidor muda a cada leitura viva, e
 * esta consulta é justamente a que precisa refletir a última.
 */
export function useRuntimeAggregateCached(): UseQueryResult<RuntimeAggregate, ApiError> {
  return useQuery<RuntimeAggregate, ApiError>({
    queryKey: runtimeKeys.agregadoCache,
    queryFn: () => get('/api/v1/runtime/status', { params: { source: 'cache' } }),
    staleTime: 0,
  });
}

/**
 * O teto da espera pela foto nova em T9: 60 releituras de 2 s, os 2 min que o servidor dá a uma leitura
 * (`in_flight_until`). Depois dele a fila fica com a foto que houver, sem afirmar mais nada.
 */
const PHOTO_SINCE_MAX_READS = 60;

/** `true` enquanto a foto do agregado for mais velha que a leitura que a tela pediu. */
function photoOlderThan(aggregate: RuntimeAggregate | undefined, since: string): boolean {
  if (!aggregate?.as_of) return true;
  return new Date(aggregate.as_of).getTime() < new Date(since).getTime();
}

/**
 * A foto do agregado, relida até ficar mais nova que `since` (aim-6ph.13, §5). É o que T9 usa para saber quando a
 * leitura do cluster que ela pediu terminou: a fila não traz bloco `reading`, e o agregado em `?source=cache` é uma
 * leitura do Postgres, sem tocar o executor (regra 11). Até 10/10/2026 T9 chamava a leitura VIVA (`source=live`),
 * que levava ~11 s no laboratório e era uma tela lendo o executor.
 *
 * `settled` é `true` quando a foto já é a da leitura pedida, quando a releitura falhou, ou quando a espera chegou ao
 * teto. `since` nulo desliga a consulta: não há leitura nova a esperar.
 */
export function useClusterPhotoSince(since: string | null): { settled: boolean } {
  const queryClient = useQueryClient();
  const queryKey = [...runtimeKeys.agregadoCache, 'since', since ?? ''] as const;
  const photo = useQuery<RuntimeAggregate, ApiError>({
    queryKey,
    queryFn: () => get('/api/v1/runtime/status', { params: { source: 'cache' } }),
    enabled: Boolean(since),
    staleTime: 0,
    refetchInterval: (query) =>
      since &&
      photoOlderThan(query.state.data, since) &&
      query.state.dataUpdateCount < PHOTO_SINCE_MAX_READS
        ? READING_POLL_MS
        : false,
  });
  if (!since || photo.isError) return { settled: true };
  if (!photo.data) return { settled: false };
  if (!photoOlderThan(photo.data, since)) return { settled: true };
  const reads = queryClient.getQueryState(queryKey)?.dataUpdateCount ?? 0;
  return { settled: reads >= PHOTO_SINCE_MAX_READS };
}

/**
 * Tudo que **lê o cache de prontidão** do backend — o que uma leitura viva do
 * cluster acabou de renovar (04/09, causa do «só atualiza com F5»):
 *
 * - T4/T5 e as listas de T13/T14/T15 (`['identities', …]`): `security_ready` e o
 *   estado derivado (`PodsNoDigestAprovado` → `aprovada_em_execucao`);
 * - T3 (`overview`): o card SecurityReady `ok/total`;
 * - T13/T14 (`contracts`): o card Estado com `security_ready`;
 * - T9 (`approvals`): o selo «em execução» da fila;
 * - a moldura (`shell`): chip do middleware e contadores;
 * - a variante rápida deste agregado, para o próximo `?source=cache` vir renovado.
 *
 * **Não** invalida `runtimeKeys.agregado`: é a própria leitura viva que chama
 * isto, e invalidá-la aqui seria refazê-la em laço.
 */
export function useInvalidateReadiness(): () => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: identitiesKeys.todas }),
      queryClient.invalidateQueries({ queryKey: overviewKeys.overview }),
      queryClient.invalidateQueries({ queryKey: contractsKeys.todas }),
      queryClient.invalidateQueries({ queryKey: approvalsKeys.todas }),
      queryClient.invalidateQueries({ queryKey: shellKeys.shell }),
      queryClient.invalidateQueries({ queryKey: runtimeKeys.agregadoCache }),
    ]);
  }, [queryClient]);
}

/** SecurityReady e os 8 checks de uma identidade (FR-090). */
export function useRuntimeStatus(id: string | null): UseQueryResult<RuntimeStatus, ApiError> {
  return useQuery<RuntimeStatus, ApiError>({
    queryKey: runtimeKeys.status(id ?? ''),
    queryFn: () =>
      get(
        withPath('/api/v1/runtime/identities/{id}/status', {
          id: id ?? '',
        }) as '/api/v1/runtime/identities/{id}/status',
      ),
    enabled: Boolean(id),
  });
}

/** Desejado × publicado no PDP (FR-092). É do tenant, não da identidade. */
export function useRuntimeAuthorization(): UseQueryResult<AuthorizationView, ApiError> {
  return useQuery<AuthorizationView, ApiError>({
    queryKey: runtimeKeys.autorizacao,
    queryFn: () => get('/api/v1/runtime/authorization'),
  });
}

/** Depois de reconciliar, tudo que se lê do executor está velho. */
function useInvalidarRuntime() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: runtimeKeys.todos });
    void queryClient.invalidateQueries({ queryKey: shellKeys.shell });
  };
}

/** **Republicar no PDP** (FR-092). */
export function useMaterializeAuthorization(): UseMutationResult<OrderResult, ApiError, void> {
  const invalidar = useInvalidarRuntime();
  return useMutation<OrderResult, ApiError, void>({
    // Ordem longa: passa pelo middleware e pelo PDP. 2 min, não os 20 s do cliente.
    mutationFn: () => post('/api/v1/runtime/materialize', { timeoutMs: TIMEOUT_ORDER_LONGAMS }),
    onSuccess: invalidar,
  });
}

/** **Reconciliar admission** (FR-092). */
export function useReconcileAdmission(): UseMutationResult<OrderResult, ApiError, void> {
  const invalidar = useInvalidarRuntime();
  return useMutation<OrderResult, ApiError, void>({
    // Idem: reescreve o ConfigMap no cluster do cliente pelo middleware.
    mutationFn: () => post('/api/v1/runtime/reconcile', { timeoutMs: TIMEOUT_ORDER_LONGAMS }),
    onSuccess: invalidar,
  });
}

/** **Aplicar imagem no Deployment** (FR-093, admin). */
export function useDeployIdentityImage(id: string): UseMutationResult<OrderResult, ApiError, void> {
  const invalidar = useInvalidarRuntime();
  return useMutation<OrderResult, ApiError, void>({
    mutationFn: () =>
      post(
        withPath('/api/v1/runtime/identities/{id}/deploy', {
          id,
        }) as '/api/v1/runtime/identities/{id}/deploy',
        { body: {} },
      ),
    onSuccess: invalidar,
  });
}
